/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { TrackingServiceV2 } from '@oneaudi/audi-tracking-service';
import { isAbsoluteURL } from '../../utils/utils';

export enum EventAction {
  INTERNAL = 'internal_link',
  EXIT = 'exit_link',
  COMPONENT_NAME = 'inpage-navigation',
  NAME = `inpage navigation`,
  SAME_LEVEL_NAVIGATION = 'same-level-navigation',
  SUB_LEVEL_NAVIGATION = 'sub-level-navigation',
  NAVIGATION = 'navigation',
  NONE_INTERACTION = 'non_interaction',
}

export interface Tracking {
  appReady(): void;
  impression(clickTrackingAttributes: ClickTrackingAttributes): void;
  toggleNavigation(clickTrackingAttributes: ClickTrackingAttributes): void;
  navigationItemClick(clickTrackingAttributes: ClickTrackingAttributes): void;
}

export type TrackingRelatedElements = {
  elementName: string;
  elementValue: string;
};

/**
 * @param {number} index - The index of the element to get the position for
 * @param {boolean} [isParentPresent=false] - Whether the main parent element is present or not
 * @returns {number} The position of the element
 */
export const getIndexPosition = (index: number, isParentPresent: boolean = false): number => {
  const indexPosition: number = isParentPresent ? index + 2 : index + 1;
  return indexPosition;
};

interface ClickTrackingAttributes {
  elementName: string;
  label: string;
  clickID: string;
  targetURL: string;
  pos: string;
  value?: string;
  isSubNavigation?: boolean;
  eventName?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  relatedElements?: TrackingRelatedElements[];
}

export function useTracking(
  trackingService: TrackingServiceV2 | undefined,
  attributes?: object,
): Tracking {
  const defaultComponentAdditions = {
    componentInfo: {
      implementer: 2,
    },
    attributes: {
      version: __FEATURE_APP_VERSION__,
      ...attributes,
    },
  };

  return {
    appReady: () => {
      if (trackingService) {
        const readyEvent = {
          event: {
            attributes: {
              componentName: EventAction.COMPONENT_NAME,
              elementName: '',
              label: '',
              clickID: '',
              pos: '',
              targetURL: '',
              value: '',
            },
            eventInfo: {
              eventAction: 'feature_app_ready',
              eventName: `${EventAction.NAME} - feature app ready`,
            },
          },
          componentUpdate: defaultComponentAdditions,
        };
        trackingService.track(readyEvent);
      }
    },

    impression: (clickTrackingAttributes) => {
      if (trackingService) {
        const impressionEvent = {
          event: {
            attributes: {
              componentName: EventAction.COMPONENT_NAME,
              elementName: '',
              label: clickTrackingAttributes.label,
              clickID: '',
              pos: '',
              targetURL: '',
              value: clickTrackingAttributes.value || EventAction.NAME,
              relatedElements: clickTrackingAttributes.relatedElements,
            },
            eventInfo: {
              eventAction: 'impression',
              eventName: `${EventAction.NAME} - impression`,
            },
          },
          componentUpdate: defaultComponentAdditions,
        };
        trackingService.track(impressionEvent);
      }
    },

    toggleNavigation: (clickTrackingAttributes) => {
      if (trackingService) {
        const toggleEvent = {
          event: {
            attributes: {
              componentName: EventAction.COMPONENT_NAME,
              elementName: clickTrackingAttributes.elementName,
              label: clickTrackingAttributes.label,
              clickID: clickTrackingAttributes.clickID,
              pos: clickTrackingAttributes.pos,
              targetURL: isAbsoluteURL(clickTrackingAttributes.targetURL)
                ? clickTrackingAttributes.targetURL
                : `${window.location.origin}${clickTrackingAttributes.targetURL}`,
              value: clickTrackingAttributes.value,
              relatedElements: clickTrackingAttributes.relatedElements || [],
            },
            eventInfo: {
              eventAction: 'navigation',
              eventName: clickTrackingAttributes.eventName,
            },
          },
          componentUpdate: defaultComponentAdditions,
        };
        trackingService.track(toggleEvent);
      }
    },

    navigationItemClick: (clickTrackingAttributes) => {
      if (trackingService) {
        const clickEvent = {
          event: {
            attributes: {
              componentName: EventAction.COMPONENT_NAME,
              elementName: clickTrackingAttributes.elementName,
              label: clickTrackingAttributes.label,
              clickID: clickTrackingAttributes.clickID,
              pos: clickTrackingAttributes.pos,
              targetURL: isAbsoluteURL(clickTrackingAttributes.targetURL)
                ? clickTrackingAttributes.targetURL
                : `${window.location.origin}${clickTrackingAttributes.targetURL}`,
              value: clickTrackingAttributes.value,
              relatedElements: clickTrackingAttributes.relatedElements || [],
            },
            eventInfo: {
              eventAction: isAbsoluteURL(clickTrackingAttributes.targetURL)
                ? 'exit_link'
                : 'internal_link',
              eventName: clickTrackingAttributes.eventName,
            },
          },
          componentUpdate: defaultComponentAdditions,
        };
        trackingService.track(clickEvent);
      }
    },
  };
}
